import * as types from '../../types';
import { isBundleCartItem } from '../cart';
import { getLocalizedPricing } from './money';

/**
 * Returns the referral subscription discount in cents for a given plan length and currency.
 * These values are cosmetic previews — the actual discount is applied by Recurly at purchase time.
 */
export function getReferralDiscountInCents(renewalMonths: number, currency?: types.FiCurrency): number {
  if (currency === types.FiCurrency.CAD) {
    switch (renewalMonths) {
      case 6:
        return 2500;
      case 12:
        return 4000;
      case 24:
        return 6000;
      default:
        return 0;
    }
  } else if (currency === types.FiCurrency.RON) {
    switch (renewalMonths) {
      case 6:
        return 7500;
      case 12:
        return 15000;
      case 24:
        return 25000;
      default:
        return 0;
    }
  } else {
    switch (renewalMonths) {
      case 6:
        return 1500;
      case 12:
        return 3000;
      case 24:
        return 5000;
      default:
        return 0;
    }
  }
}

/**
 * Calculates the total referral discount for all bundle items in the cart.
 * Includes both the subscription price discount and waived activation fees.
 * Only applies to non-free-trial module subscription products.
 */
export function getCartReferralDiscountInCents(
  cart: types.Cart,
  productSkuMap: Map<string, { product: types.IProduct }>,
  currency: types.FiCurrency,
): number {
  return Object.values(cart.cartItems).reduce((total, cartItem) => {
    if (!isBundleCartItem(cartItem)) return total;

    const subEntry = productSkuMap.get(cartItem.subscriptionLineItem.sku);
    if (!subEntry || !types.isModuleSubscriptionProduct(subEntry.product)) return total;

    const subscriptionProduct = subEntry.product;
    if (subscriptionProduct.freeTrial) return total;

    const subscriptionDiscount = getReferralDiscountInCents(subscriptionProduct.renewalMonths, currency);
    const activationFeeDiscount = subscriptionProduct.activationFeePricingInCents
      ? getLocalizedPricing(subscriptionProduct.activationFeePricingInCents, currency)
      : 0;

    return total + subscriptionDiscount + activationFeeDiscount;
  }, 0);
}
